import { lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { useEventListener } from 'usehooks-ts';
import gtag from '../analytics/gtag';
import { search as query } from '../api/search';
import Page from '../generic/Page';
import useUser from '../login/userContext';
import type { QueryParams, SearchData} from '../model/Search';
import { getPageTitle, getSearchData, updateHistory } from '../model/Search';
import type { User } from '../model/types';
import { preconnect } from '../utils/HeaderContext';
import * as url from '../utils/url';
import { useAsyncAction } from '../utils/useAsyncAction';
import useOnMount from '../utils/useOnMount';
import { SearchTypes } from './SearchType';

interface Props {
    pageTitle: string;
}

export default function SearchPage({ pageTitle }: Props) {
    const { user } = useUser();
    const [data, setData] = useState<SearchData | null>(null);

    // Remember if we have fetched both product and content search results, so that we can switch between them without having to fetch both again.
    // This will make filtering and sorting faster, since we only need to fetch one of the search results.
    // The other, unselected search result, will only be used to show the number of results, and filtering and sorting the selected search does not affect the number of results of the unselected search.
    const hasFetchedBoth = useRef(false);

    const [search, isLoading, error] = useAsyncAction(
        async (params: Partial<QueryParams>, user: Readonly<User> | null | undefined, history: 'push' | 'replace' | 'nothing' = 'push') => {
            const newParams = {
                query: ':relevance:',
                currentPage: '0',
                ...url.getParams(),
                ...params,
            };

            const paramsToBackend = new URLSearchParams({
                fields: 'FULL',
                pageSize: '24',
                ...newParams,
            });

            // Use the selected searchType after we have fetched both
            paramsToBackend.set('searchTypes', hasFetchedBoth.current ? (paramsToBackend.get('searchType') ?? 'product') : 'product,content');
            paramsToBackend.delete('searchType');
            // Don't send the previousKeyword after the initial load, since it prevents facets from working
            if (hasFetchedBoth.current) {
                paramsToBackend.delete('previousKeyword');
            }

            const { productSearchResult, contentSearchResult } = await query(paramsToBackend);
            const redirectTo = productSearchResult?.keywordRedirectUrl && URL.parse(productSearchResult.keywordRedirectUrl, location.href);
            if (redirectTo) {
                if (redirectTo.pathname === location.pathname) {
                    search(url.getParams(redirectTo.searchParams), user, 'replace');
                } else {
                    window.location.assign(redirectTo);
                }
                return;
            }

            const data = await getSearchData(
                newParams.searchType ?? 'product',
                productSearchResult,
                contentSearchResult,
                user,
                !hasFetchedBoth.current,
            );

            if (!data) return;

            // Remember that we have fetched both product and content search results, so that we can switch between them without having to fetch both again.
            hasFetchedBoth.current ||= !!(data.contentSearchResult && data.productSearchResult);

            setData((old) => {
                if (data.searchType === 'content') {
                    return {
                        ...data,
                        productSearchResult: data.productSearchResult ?? old?.productSearchResult,
                    };
                } else {
                    return {
                        ...data,
                        contentSearchResult: data.contentSearchResult ?? old?.contentSearchResult,
                    };
                }
            });

            gtag('event', 'view_search_results', {
                filter_string: data.currentQuery,
                search_term: data.currentSearchTerm,
                num_results: data.pagination.totalResults,
            });

            if (history !== 'nothing') {
                updateHistory(
                    new URLSearchParams({
                        ...newParams,
                        searchType: data.searchType,
                        query: data.currentQuery,
                        currentPage: data.pagination.currentPage.toString(),
                    }),
                    history === 'replace',
                );
            }
        },
        true,
    );

    const searchWithUser = useMemo(
        () =>
            (params: Partial<QueryParams>, history: 'push' | 'replace' | 'nothing' = 'push') =>
                search(params, user, history),
        [search, user],
    );

    useEventListener('submit', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLFormElement)) return;
        if (target.id !== 'header-search-form') return;
        event.preventDefault();
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        const formData = new FormData(target);
        const query = formData.get('q')?.toString() ?? '';
        hasFetchedBoth.current = false;
        setData(null);
        searchWithUser({ currentPage: '0', query });
    });

    const setSearchType = useCallback(
        (searchType: 'product' | 'content', query: string) => {
            if (searchType === 'content') {
                searchWithUser({ searchType, currentPage: '0', query });
            } else {
                searchWithUser({ searchType, currentPage: '0', query });
            }
        },
        [searchWithUser, searchWithUser],
    );

    useOnMount(() => {
        history.scrollRestoration = 'manual';
        searchWithUser({}, 'replace');
    });

    useEventListener('popstate', () => searchWithUser({}, 'nothing'));

    // Preconnect to the subdomain that contains the product images
    preconnect('https://bilder.vinmonopolet.no');

    if (!data) {
        return <Page isSpinning={isLoading} error={error} />;
    }

    const { productSearchResult, contentSearchResult, currentQuery, searchType, currentSearchTerm } = data;

    return (
        <Suspense
            fallback={
                <Page isSpinning title={getPageTitle(currentSearchTerm, pageTitle)}>
                    <SearchTypes selected={searchType} product={productSearchResult} content={contentSearchResult} onSelect={setSearchType} />
                </Page>
            }
        >
            <Page isSpinning={isLoading} error={error} title={getPageTitle(currentSearchTerm, pageTitle)}>
                <SearchTypes selected={searchType} product={productSearchResult} content={contentSearchResult} onSelect={setSearchType} />
                {searchType === 'product' ? (
                    <ProductSearch productSearchResult={productSearchResult} currentQuery={currentQuery} search={searchWithUser} />
                ) : (
                    <ContentSearch contentSearchResult={contentSearchResult} currentQuery={currentQuery} search={searchWithUser} />
                )}
            </Page>
        </Suspense>
    );
}

const ContentSearch = lazy(() => import('./ContentSearch'));
const ProductSearch = lazy(() => import('./ProductSearch'));
